import type { Cell } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import type { Table as TableType } from "@tanstack/table-core/build/lib/types";
import clsx from "clsx";

import TableBodyCell from "~/components/Table/components/TableBodyCell";

type DefaultTableBodyContentProps<T> = {
    disableUnselectableRows?: boolean;
    fontSize?: "sm" | "xs";
    table: TableType<T>;
    verticalBorders?: boolean;
};

const DefaultTableBodyContent = <T,>({
    disableUnselectableRows = false,
    fontSize = "sm",
    table,
    verticalBorders,
}: DefaultTableBodyContentProps<T>) => {
    return table.getRowModel().rows.map((row) => {
        const onRowClick = table.options.meta ? table.options.meta.onRowClick : undefined;
        const rowIsSelected = row.getIsSelected();
        const rowCannotBeSelected = disableUnselectableRows ? !row.getCanSelect() : null;
        return (
            <tr
                className={clsx(
                    "group",
                    rowCannotBeSelected === true && "pointer-events-none opacity-30",
                    onRowClick && "cursor-pointer",
                    verticalBorders &&
                        "divide-x divide-gray-200 hover:divide-white",
                    rowIsSelected && "bg-primary-200",
                )}
                key={row.id}
                onClick={onRowClick ? (event) => onRowClick(row, event) : undefined}
            >
                {row.getVisibleCells().map((cell: Cell<T, any>) => {
                    return (
                        <TableBodyCell
                            key={cell.id}
                            fontSize={fontSize}
                            textAlign={cell.column.columnDef.meta?.cellTextAlign}
                            hasWidthRestriction={cell.column.columnDef.meta?.width !== undefined}
                            hasPadding={cell.column.columnDef.meta?.hasPadding}
                            className={clsx(
                                cell.column.getIsPinned() && "sticky left-0 z-10 bg-white",
                                rowIsSelected
                                    ? "group-hover:bg-primary-300"
                                    :  "group-hover:bg-gray-200",
                            )}
                        >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableBodyCell>
                    );
                })}
            </tr>
        );
    });
};

export default DefaultTableBodyContent;
